import React, {
  HTMLAttributes,
  HtmlHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import autoAnimate from "@formkit/auto-animate";
import { constants } from "./constants";
import { TrackWrapper } from "./track/wrapper";

const searchParams = new URLSearchParams(window.location.search);
interface Filter {
  type: "primaryColor" | "material" | "pattern" | "subcategory";
  value: string[];
}
const categoryToFilterType = {
  color: "primaryColor",
  material: "material",
  pattern: "pattern",
  gender: "gender",
};
type FilterState = MAP<Filter["type"], Filter["value"]>;
export default function Filters({
  isOpen = true,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const [appliedFilters, setAppliedFilters] = useState<
    Map<Filter["type"], Filter["value"]>
  >(new Map());
  const [selectedFilters, setSelectedFilters] = useState<FilterState>(
    new Map(),
  );
  const [filtersData, setFiltersData] = useState<Filter[]>([]);
  const [tab, setTab] = useState<FilterCategories>("color");
  const [enabled, setEnabled] = useState(true);
  const parentRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (parentRef.current) {
      autoAnimate(parentRef.current, {
        duration: 300,
        easing: "ease-in-out",
      });
    }
  }, [parentRef]);

  useEffect(() => {
    const updatedState: { [key: string]: boolean } = {};
    setInterval(() => {
      const data = window.helium.datastore.getFilterData();
      console.log({ data }, "filters");
      setFiltersData(data);
    }, 2000);
    // update  appliedFilters based on the search params
  }, []);

  const handleFilterClick = (key: string, val: string) => {
    const value = val.toLowerCase();
    let hasChanged = false;
    if (!value) {
      //delete filterValues from values of type
      //if the values is empty, delete the filter
      hasChanged = true;
    } else {
      // already exists: do nothing
      // new: add to the map
      // exists but new value: add to the map
    }
    const map = new Map(selectedFilters);
    const data = map.get(key);
    if (data) {
      const idx = data.indexOf(value);
      if (idx !== -1) {
        data.splice(idx, 1);
        if (data.length === 0) map.delete(key);
      } else {
        data.push(value);
        map.set(key, data);
      }
    } else {
      map.set(key, [value]);
    }
    hasChanged = true;

    if (hasChanged) {
      setSelectedFilters(map);
      //setSearchParams(map);
    }
  };

  function setSearchParams(newAppliedFilters: typeof appliedFilters) {
    console.log({ newAppliedFilters });
    const searchParams = new URLSearchParams(window.location.search);
    const keysToDelete: string[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith("s_")) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => searchParams.delete(key));
    newAppliedFilters.forEach((value, type) => {
      value?.forEach?.((val) => {
        searchParams.append("s_" + type, val);
      });
    });

    window.history.replaceState(null, "", `?${searchParams.toString()}`);
    window.helium.events.notify({ data: { type: "infer filter" } });
  }

  const hasEnabledFilters = useMemo(() => {
    if (appliedFilters.size === 0) {
      setSearchParams([]);
      setSelectedFilters(new Map());
      return false;
    } else {
      setSearchParams(appliedFilters);
      setSelectedFilters(new Map(appliedFilters));
      return true;
    }
  }, [appliedFilters]);

  if (!enabled || filtersData.length < 2) return null;
  if (!hasEnabledFilters) {
    return (
      <TrackWrapper
        event={{
          name: "inferred-filter",
          properties: {
            type: "inferred-filter",
          },
        }}
      >
        <CurateSection
          filtersData={filtersData}
          setAppliedFilters={setAppliedFilters}
          setEnabled={setEnabled}
        />
      </TrackWrapper>
    );
  }

  const { color, material, pattern, subcategory } = useMemo(() => {
    const color = appliedFilters.get("primaryColor")?.join?.(", ");
    const material = appliedFilters.get("material")?.join?.(", ");
    const pattern = appliedFilters.get("pattern")?.join?.(", ");
    const filters = appliedFilters;
    const subcategory = (
      window.helium.datastore.current.page.filters?.subcategory || ""
    )
      .split("/")
      .at(-1);
    return { color, material, pattern, subcategory };
  }, [appliedFilters]);
  const text = `Curated ${[color, pattern, material, subcategory]
    .filter(Boolean)
    .join(" ")}`;

  const filterCount = useMemo(() => {
    const filterMap = selectedFilters;
    let count = 0;
    for (const key of filterMap.keys()) {
      const data = filterMap.get(key);
      count += data.length;
    }
    return count;
  }, [selectedFilters]);

  return (
    <div className="hp-fixed hp-inset-x-0 hp-bottom-0 hp-z-10 hp-w-full hp-translate-y-0 hp-bg-white hp-text-black hp-shadow-lg hp-transition-transform hp-duration-300">
      <div className="hp-relative">
        <div className="hp-flex hp-items-center hp-justify-between hp-gap-2 hp-border-t hp-border-black hp-px-2 hp-text-sm">
          {text}
        </div>
        <div className="hp-flex hp-justify-between hp-gap-2 hp-border-y hp-border-black hp-px-2 hp-py-2">
          {categories.map((category) => (
            <TabButton
              key={category}
              onClick={() => {
                setTab(category);
              }}
            >
              <Pillet
                style={{
                  width: "100%",
                  backgroundColor: tab === category ? "black" : "white",
                  color: tab === category ? "white" : "black",
                  textDecoration: "none",
                }}
              >
                {category}
              </Pillet>
            </TabButton>
          ))}
        </div>
        <div>
          <TabContent
            category={tab}
            appliedFilters={selectedFilters}
            onSelect={(value: string) => {
              const key = categoryToFilterType[tab];
              console.log("to be added", { value });
              handleFilterClick(key, value);
            }}
          />
        </div>
        <div className="hp-flex hp-justify-end hp-border-t hp-border-black">
          <button
            onClick={() => setAppliedFilters(new Map())}
            className="w-full py-2 hover:opacity-80 active:opacity-60"
          >
            Clear
          </button>
          <button
            onClick={() => setAppliedFilters(new Map(selectedFilters))}
            className="helium-gradient w-full border-l border-black py-2 hover:opacity-80 active:opacity-60"
          >
            {`Curate ${filterCount === 0 ? "" : `(${filterCount})`}`}
          </button>
        </div>
      </div>
    </div>
  );
}
function CurateSection({ filtersData, setAppliedFilters, setEnabled }) {
  const color = filtersData.find((val) => val.type === "primaryColor");
  const material = filtersData.find((val) => val.type === "material");
  const pattern = filtersData.find((val) => val.type === "pattern");
  const subcategory = (
    window.helium.datastore.current.page.filters?.subcategory || ""
  )
    .split("/")
    .at(-1);
  function pilletClick({ type, value }: { type: string; value: string }) {
    const map = new Map();
    map.set(type, [value]);
    setAppliedFilters(map);
  }
  const handleCurate = React.useCallback(() => {
    const map = new Map();
    if (color?.value) map.set("primaryColor", [color.value]);
    if (pattern?.value) map.set("pattern", [pattern.value]);
    if (material?.value) map.set("material", [material.value]);
    setAppliedFilters(map);
  }, [color, pattern, material, setAppliedFilters]);

  return (
    <div className="hp-fixed hp-inset-x-0 hp-bottom-0 hp-z-10 hp-flex hp-w-full hp-translate-y-0 hp-items-center hp-rounded-t-2xl hp-border-t hp-bg-white hp-p-4 hp-text-black hp-shadow-lg hp-transition-transform hp-duration-300">
      <div className="hp-relative hp-flex hp-items-center hp-gap-2">
        <button
          className="hp-absolute hp-size-4 hp-rounded-full hp-bg-red-200 hp-text-xs"
          style={{
            top: "-16px",
            right: "-6px",
            display: "none",
          }}
          onClick={() => setEnabled(false)}
        >
          <svg
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
            class=""
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div className="hp-text-pretty">
          <span>We figured you are looking at </span>
          {[
            { type: "primaryColor", value: color?.value },
            { type: "material", value: material?.value },
            { type: "pattern", value: pattern?.value },
            { type: "subcategory", value: subcategory },
          ].map(({ type, value }) => {
            if (!value) return null;
            return (
              <Pillet key={type} onClick={() => pilletClick({ type, value })}>
                {value}
              </Pillet>
            );
          })}
        </div>
        <button className="hp-helium-btn" onClick={handleCurate}>
          Curate
        </button>
      </div>
    </div>
  );
}

type FilterCategories = "color" | "material" | "pattern" | "gender";
const categories: FilterCategories[] = [
  "color",
  "material",
  "pattern",
  "gender",
];
function TabButton({
  children,
  selected,
  ...props
}: HTMLAttributes<HTMLButtonElement> & { selected: boolean }) {
  return (
    <button className="hp-w-full" {...props}>
      {children}
    </button>
  );
}

const lists = {
  colors: [
    "Red",
    "Brown",
    "Blue",
    "Green",
    "Black",
    "White",
    "Yellow",
    "Pink",
    "Purple",
    "Orange",
    "Grey",
    "Beige",
    "Navy",
    "Gold",
    "Silver",
  ],
  material: [
    "Cotton",
    "Organic Cotton",
    "Wool",
    "Merino Wool",
    "Cashmere",
    "Polyester",
    "Silk",
    "Satin",
    "Denim",
    "Linen",
    "Leather",
    "Faux Leather",
    "Suede",
    "Nylon",
    "Rayon",
    "Viscose",
    "Spandex",
    "Elastane",
    "Lycra",
    "Velvet",
    "Corduroy",
    "Tweed",
    "Chiffon",
    "Georgette",
    "Jersey",
    "Fleece",
    "Modal",
    "Tencel",
    "Bamboo",
    "Hemp",
    "Acrylic",
    "Acetate",
    "Neoprene",
    "Gore-Tex",
    "Mesh",
    "Lace",
    "Tulle",
    "Felt",
    "Flannel",
    "Terry cloth",
    "Sequin fabric",
    "Metallic fabric",
  ],
  pattern: [
    "Solid",
    "Striped",
    "Vertical stripes",
    "Horizontal stripes",
    "Pinstripe",
    "Checked",
    "Plaid",
    "Gingham",
    "Houndstooth",
    "Floral",
    "Abstract floral",
    "Tropical",
    "Geometric",
    "Polka dot",
    "Paisley",
    "Animal print",
    "Leopard",
    "Zebra",
    "Snake",
    "Abstract",
    "Camouflage",
    "Tie-dye",
    "Ombre",
    "Color block",
    "Herringbone",
    "Argyle",
    "Chevron",
    "Jacquard",
    "Ikat",
    "Toile",
    "Damask",
    "Tribal",
    "Medallion",
    "Patchwork",
    "Hawaiian",
    "Mosaic",
    "Graphic print",
    "Logo print",
    "Text print",
    "Photographic print",
    "Digital print",
    "Watercolor",
    "Batik",
    "Shibori",
  ],
  gender: ["men", "women"],
};

function TabContent({
  children,
  category,
  appliedFilters,
  onSelect,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  category: (typeof categories)[number];
  appliedFilters: FilterState;
  onSelect: (value: string) => void;
}) {
  const list = useMemo(() => {
    const list = [];
    const valuesSet = new Set();

    appliedFilters.forEach((value) => {
      value?.forEach?.((val) => valuesSet.add(val.toLowerCase()));
    });
    console.log({ valuesSet });

    if (category === "color") {
      let arr = lists.colors;
      arr = arr.sort((a, b) => {
        const aval = valuesSet.has(a.toLowerCase()) ? 1 : 0;
        const bval = valuesSet.has(b.toLowerCase()) ? 1 : 0;
        return bval - aval;
      });
      list.push(
        ...arr.map((color) => (
          <div
            key={color}
            className="hp-flex hp-flex-col hp-items-center hp-rounded hp-px-2 hp-py-1 hp-text-sm hp-font-semibold"
            style={{
              border: valuesSet.has(color.toLowerCase())
                ? "1px solid black"
                : "1px solid white",
            }}
            onClick={() => {
              onSelect(color);
            }}
          >
            <div
              className="hp-size-8 hp-rounded-full"
              style={{ backgroundColor: colorsToHex[color] }}
            ></div>
            {color}
          </div>
        )),
      );
    } else {
      let arr = lists[category];
      arr = arr.sort((a, b) => {
        const aval = valuesSet.has(a.toLowerCase()) ? 1 : 0;
        const bval = valuesSet.has(b.toLowerCase()) ? 1 : 0;
        return bval - aval;
      });
      list.push(
        ...arr.map((val, index) => (
          <div
            key={index + category}
            className="hp-flex hp-flex-col hp-items-center hp-justify-center hp-rounded hp-px-2 hp-py-1 hp-text-center hp-text-sm hp-font-semibold hp-capitalize"
            style={{
              border: valuesSet.has(val.toLowerCase())
                ? "1px solid black"
                : "1px solid white",
            }}
            onClick={() => {
              onSelect(val);
            }}
          >
            {val}
          </div>
        )),
      );
    }

    return list;
  }, [category, appliedFilters]);
  return (
    <div className="swimlane2 hp-flex hp-flex-row hp-gap-4 hp-p-1" {...props}>
      {list}
    </div>
  );
}

const colorsToHex = {
  Red: "#FF6961",
  Brown: "#CD853F",
  Blue: "#AEC6CF",
  Green: "#77DD77",
  Black: "#AAAAAA", // Light grey as a pastel version of black
  White: "#F5F5F5",
  Yellow: "#FDFD96",
  Pink: "#FFB6C1",
  Purple: "#C3B1E1",
  Orange: "#FFB347",
  Grey: "#D3D3D3",
  Beige: "#F5F5DC",
  Navy: "#AFCBFF", // Light version of Navy Blue
  Gold: "#F9E79F",
  Silver: "#C0C0C0",
};

function Pillet({ children, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className="hp-mx-1 hp-inline-block hp-rounded-full hp-text-sm hp-capitalize hp-text-purple-700 hp-underline"
      {...props}
    >
      {children}
    </span>
  );
}
const defaultFilterData = [
  {
    type: "primaryColor",
    value: "Red",
  },
  {
    type: "material",
    value: "Cotton",
  },
  {
    type: "pattern",
    value: "Solid",
  },
  {
    type: "pattern",
    value: "Striped",
  },
  {
    type: "primaryColor",
    value: "Blue",
  },
];
